import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLink } from "~/common/components/Link";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import StockIndicator, { StockLevel } from "~/domains/product/components/StockIndicator/StockIndicator";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  styled,
} from "~/common/mui";
import { URL_CONTACT_US } from "~/utils/url/staticUrls";
import type { ConfiguratorSummaryController } from "./useConfiguratorSummaryController";
import type { ConfiguratorSummaryActionId } from "./summaryCapabilities";
import { ConfiguratorPreviewImage } from "../preview";

export interface ConfiguratorSummaryProps {
  controller: ConfiguratorSummaryController;
  onEditSection?: (sectionKey: string) => void;
  onCadOpened?: () => void;
}

export interface ConfiguratorSummaryViewProps extends ConfiguratorSummaryProps {
  initialTab?: "product" | "cad";
}

const SummaryRoot = styled(Card)(({ theme }) => ({
  minWidth: 0,
  padding: theme.spacing(2.5),
  border: 0,
  borderRadius: theme.spacing(2),
  boxShadow: "none",
  backgroundColor: theme.palette.surface.bright,
}));

const SummaryHero = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(280px, 1fr) minmax(280px, 0.9fr) minmax(320px, 0.95fr)",
  gap: theme.spacing(3),
  alignItems: "start",
  [theme.breakpoints.down("desktop-m")]: {
    gridTemplateColumns: "minmax(260px, 1fr) minmax(320px, 1fr)",
  },
}));

const ImageCard = styled(Card)(({ theme }) => ({
  minWidth: 0,
  overflow: "hidden",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  boxShadow: "none",
}));

const ProductCopy = styled(Stack)(({ theme }) => ({
  minWidth: 0,
  paddingTop: theme.spacing(2),
  [theme.breakpoints.down("desktop-m")]: {
    gridColumn: "1 / -1",
    gridRow: 2,
    paddingTop: 0,
  },
}));

const CommerceCard = styled(Card)(({ theme }) => ({
  width: 480,
  maxWidth: "100%",
  boxSizing: "border-box",
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  boxShadow: "none",
}));

const TabsArea = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const InformationPanel = styled(Stack)(({ theme }) => ({ paddingTop: theme.spacing(4) }));
const SectionHeader = styled(Stack)(({ theme }) => ({
  minHeight: theme.spacing(6),
  alignItems: "center",
  justifyContent: "flex-start",
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.surfaceContainer.low,
}));
const SummaryRow = styled(Box, { shouldForwardProp: (prop) => prop !== "striped" })<{ striped: boolean }>(
  ({ striped, theme }) => ({
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    backgroundColor: striped ? theme.palette.surfaceContainer.low : theme.palette.surfaceContainer.lowest,
  }),
);
const SummaryCell = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  padding: theme.spacing(1.5, 2),
  overflowWrap: "anywhere",
}));

const actionTranslation: Record<ConfiguratorSummaryActionId, [string, string]> = {
  requestQuotation: ["feature.configurator.requestQuote.btn.open", "Request quotation"],
  requestOffer: ["linearConfigSummary.card.requestOffer", "Request offer"],
  saveConfiguration: ["linearConfigSummary.card.saveConfig", "Save configuration"],
  downloadDatasheet: ["linearConfigSummary.card.downloadDatasheet", "Download datasheet"],
  addToCart: ["linearConfigSummary.card.addToCart", "Add items to cart"],
  addToQuote: ["linearConfigSummary.card.addToQuote", "Add to quote"],
  goToCart: ["page.linear.addToCart.button.goToCart", "Go to cart"],
  contactSpecialist: ["linearConfigSummary.card.contactSpecialist", "Contact specialist"],
};

const actionIcon = (action: ConfiguratorSummaryActionId) => {
  if (action === "addToCart" || action === "addToQuote" || action === "goToCart") {
    return <ShoppingCartOutlinedIcon />;
  }
  if (action === "saveConfiguration") {
    return <SaveOutlinedIcon />;
  }
  if (action === "downloadDatasheet") {
    return <DownloadRoundedIcon />;
  }
  return undefined;
};

export const ConfiguratorSummaryView = ({
  controller,
  onEditSection,
  onCadOpened,
  initialTab = "product",
}: ConfiguratorSummaryViewProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"product" | "cad">(initialTab);
  const { viewModel } = controller;
  const { commerce } = viewModel;

  const priceContent = (() => {
    switch (commerce.price.state) {
      case "hidden":
        return null;
      case "loading":
        return <CircularProgress size={24} />;
      case "error":
        return <Alert severity="error">{t("hybrisAjaxError")}</Alert>;
      case "onRequest":
        return (
          <Typography variant="headlineSmall" sx={{ fontWeight: 700 }}>
            {t("product.priceOnRequest", "Price on request")}
          </Typography>
        );
      case "value":
        return (
          <Typography variant="headlineSmall" sx={{ fontWeight: 700 }}>
            {commerce.price.text}
          </Typography>
        );
    }
  })();

  const availabilityContent = (() => {
    switch (commerce.availability.state) {
      case "hidden":
        return null;
      case "loading":
        return <CircularProgress size={20} />;
      case "error":
        return <Alert severity="error">{t("hybrisAjaxError")}</Alert>;
      case "value": {
        const availabilityText = commerce.availability.leadTimeWeeks
          ? t("linearConfigSummary.card.avaliability", { count: commerce.availability.leadTimeWeeks })
          : commerce.availability.text;
        return (
          <Box sx={{ alignSelf: "flex-start" }}>
            <StockIndicator.V2
              level={commerce.availability.level ?? StockLevel.NOT_IN_STOCK}
              changeText={availabilityText}
            />
          </Box>
        );
      }
    }
  })();

  return (
    <SummaryRoot data-configurator-summary>
      <SummaryHero>
        <ImageCard>
          <ConfiguratorPreviewImage imageUrl={viewModel.imageUrl} designation={viewModel.designation} />
        </ImageCard>
        <ProductCopy gap={1.5}>
          <Typography variant="headlineSmall" sx={{ fontWeight: 700 }}>
            {viewModel.designation ?? t("feature.configurator.v2.summary.unnamed", "Configured product")}
          </Typography>
          {viewModel.description && (
            <Typography variant="bodyLarge" sx={{ color: "text.secondary" }}>
              {viewModel.description}
            </Typography>
          )}
        </ProductCopy>
        <CommerceCard>
          <Stack gap={2}>
            {priceContent}
            {commerce.price.state !== "hidden" && (
              <Typography variant="bodySmall" sx={{ color: "text.secondary" }}>
                {t("linearConfigSummary.pdp.perSystem")}
              </Typography>
            )}
            {commerce.showPriceReference && (
              <TextField
                id="configurator-summary-price-reference"
                select
                fullWidth
                disabled={!commerce.priceReferenceEditable}
                size="small"
                value={commerce.priceReference}
                label={t("feature.configurator.v2.summary.priceReference", "Price reference")}
                onChange={(event) => controller.setPriceReference(event.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                  select: { displayEmpty: true },
                }}
              >
                {commerce.priceReferences.map((reference) => (
                  <MenuItem key={reference.value} value={reference.value}>
                    {reference.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {commerce.showQuantity && (
              <QuantityInput
                value={commerce.quantity}
                min={1}
                step={1}
                persistentMinValue
                variant="compact"
                size="small"
                fullWidth
                onValueChange={controller.setQuantity}
              />
            )}
            {availabilityContent}
            {commerce.quantitySettling && (
              <Typography variant="bodySmall" color="text.secondary">
                {t("feature.configurator.v2.summary.updatingCommerce", "Updating price and availability…")}
              </Typography>
            )}
            {commerce.actionError && <Alert severity="error">{commerce.actionError}</Alert>}
            {commerce.actions
              .filter((action) => action.visible)
              .map((action) => {
                const [key, fallback] = actionTranslation[action.id];
                if (action.id === "contactSpecialist") {
                  return (
                    <ArrowLink
                      key={action.id}
                      href={commerce.contactSpecialistUrl || URL_CONTACT_US}
                      target="_blank"
                      disabled={!action.enabled}
                    >
                      {t(key, fallback)}
                    </ArrowLink>
                  );
                }
                return (
                  <Button
                    key={action.id}
                    fullWidth
                    size="large"
                    variant={action.variant}
                    color={action.color}
                    startIcon={actionIcon(action.id)}
                    disabled={!action.enabled}
                    onClick={() => controller.triggerAction(action.id)}
                  >
                    {t(key, fallback)}
                  </Button>
                );
              })}
          </Stack>
        </CommerceCard>
      </SummaryHero>

      <TabsArea>
        <Tabs
          value={activeTab}
          variant="fullWidth"
          fullWidthIndicator={false}
          onChange={(_, value: "product" | "cad") => {
            setActiveTab(value);
            if (value === "cad") {
              onCadOpened?.();
            }
          }}
        >
          <Tab value="product" label={t("feature.configurator.v2.summary.productInformation", "Product information")} />
          {commerce.showCad && <Tab value="cad" label={t("feature.configurator.v2.summary.cad", "CAD")} />}
        </Tabs>
      </TabsArea>

      {activeTab === "product" ? (
        <InformationPanel gap={3} role="tabpanel" aria-label="Product information">
          {viewModel.sections.map((section) => (
            <Box key={section.key}>
              <SectionHeader direction="row" gap={1}>
                <Typography variant="titleMedium" sx={{ fontWeight: 700 }}>
                  {section.title}
                </Typography>
                {onEditSection && (
                  <IconButton
                    color="primary"
                    aria-label={t("feature.configurator.v2.summary.editSection", {
                      defaultValue: "Edit {{section}}",
                      section: section.title,
                    })}
                    onClick={() => onEditSection(section.key)}
                  >
                    <EditRoundedIcon />
                  </IconButton>
                )}
              </SectionHeader>
              <Divider />
              {section.rows.map((row, index) => (
                <SummaryRow key={row.key} striped={index % 2 === 1}>
                  <SummaryCell variant="bodyMedium">{row.label}</SummaryCell>
                  <SummaryCell variant="bodyMedium">{row.value}</SummaryCell>
                </SummaryRow>
              ))}
            </Box>
          ))}
        </InformationPanel>
      ) : (
        <InformationPanel role="tabpanel" aria-label="CAD">
          <Typography variant="bodyLarge">
            {t("feature.configurator.v2.summary.cadPlaceholder", "CAD content will be available here.")}
          </Typography>
        </InformationPanel>
      )}
    </SummaryRoot>
  );
};

export const ConfiguratorSummary = (props: ConfiguratorSummaryProps) => (
  <ConfiguratorSummaryView
    {...props}
    initialTab={new URLSearchParams(window.location.search).get("showCAD") === "true" ? "cad" : "product"}
  />
);
